import { useEffect, useRef, useState } from 'react';
import { getContactSettings } from '@/lib/contactSettings';
import { saveComplaint } from '@/lib/supabase';

const complaintTypes = [
  'Order Not Delivered',
  'Wrong Product Received',
  'Product Quality Issue',
  'Refund/Return Request',
  'Payment Issue',
  'Delivery Delay',
  'Size/Fit Issue',
  'Other',
];

export default function Contact() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', complaintType: '', orderId: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success'>('idle');

  // Load dynamic contact settings
  const cs = getContactSettings();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.unobserve(entry.target); } },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Save to Supabase
    await saveComplaint({
      name: formData.name,
      phone: formData.phone,
      complaint_type: formData.complaintType,
      order_id: formData.orderId || undefined,
      message: formData.message,
    });
    // Also send to Readdy form as backup
    try {
      const form = e.target as HTMLFormElement;
      const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
      await fetch('https://readdy.ai/api/form/d76bdcjq7u9ee9hceccg', { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: data.toString() });
    } catch { /* ignore */ }
    setIsSubmitting(false);
    setSubmitStatus('success');
    setFormData({ name: '', phone: '', complaintType: '', orderId: '', message: '' });
    setTimeout(() => setSubmitStatus('idle'), 5000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Build quick contacts dynamically based on admin settings
  const allContacts = [
    cs.whatsappEnabled
      ? { icon: 'ri-whatsapp-fill', label: 'WhatsApp', sub: cs.phone, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-100', href: `https://wa.me/${cs.phone.replace(/\D/g, '')}` }
      : null,
    { icon: 'ri-phone-fill', label: 'Call Us', sub: cs.phone, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-100', href: `tel:${cs.phone.replace(/\s/g, '')}` },
    { icon: 'ri-instagram-fill', label: 'Instagram', sub: cs.instagramId, color: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-100', href: `https://instagram.com/${cs.instagramId.replace('@', '')}` },
  ].filter(Boolean) as { icon: string; label: string; sub: string; color: string; bg: string; border: string; href: string }[];

  return (
    <section id="contact" ref={sectionRef} className="relative py-16 md:py-24 bg-white dark:bg-slate-900 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-slate-700 to-transparent"></div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-5xl mx-auto">

          {/* Header */}
          <div className={`text-center mb-10 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 mb-4">
              <i className="ri-customer-service-line text-red-500 text-xs"></i>
              <span className="text-red-600 dark:text-red-400 text-xs font-bold uppercase tracking-wider">24/7 Support</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-black text-gray-900 dark:text-white mb-3">
              Customer <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600">Support</span>
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-base max-w-xl mx-auto">Facing any issue? Fill the form and our support team will call you back within a few hours.</p>
          </div>

          <div className={`grid lg:grid-cols-5 gap-8 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>

            {/* Left: Quick Contact + Info */}
            <div className="lg:col-span-2 space-y-4">
              {allContacts.map(c => (
                <a key={c.label} href={c.href} target="_blank" rel="nofollow noopener noreferrer"
                  className={`flex items-center gap-4 p-4 rounded-2xl ${c.bg} border ${c.border} dark:bg-slate-800 dark:border-slate-700 hover:-translate-y-0.5 transition-all cursor-pointer`}>
                  <div className={`w-11 h-11 rounded-xl ${c.bg} border ${c.border} dark:bg-slate-700 dark:border-slate-600 flex items-center justify-center shrink-0`}>
                    <i className={`${c.icon} ${c.color} text-xl`}></i>
                  </div>
                  <div>
                    <p className="text-gray-900 dark:text-white font-bold text-sm">{c.label}</p>
                    <p className="text-gray-500 dark:text-gray-400 text-xs">{c.sub}</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-gray-400 dark:text-gray-500 ml-auto"></i>
                </a>
              ))}

              {/* Info card */}
              <div className="rounded-2xl overflow-hidden">
                <div className="p-5 relative" style={{ background: 'linear-gradient(135deg, #1c1917, #292524)' }}>
                  <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle, #fff 1.5px, transparent 1.5px)', backgroundSize: '20px 20px' }}></div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-orange-500/20 border border-orange-500/30 flex items-center justify-center">
                        <i className="ri-time-fill text-orange-400 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-white font-bold text-sm">Response Time</p>
                        <p className="text-white/50 text-xs">Usually within 2-4 hours</p>
                      </div>
                    </div>
                    <p className="text-white/60 text-xs leading-relaxed">Our team is available Mon–Sun, 9AM–10PM. For urgent issues, WhatsApp is fastest.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-3">
              <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700 p-7">
                {/* Form Header */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
                    <i className="ri-headphone-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-gray-900 dark:text-white font-black text-lg">Register Complaint</h3>
                    <p className="text-gray-400 dark:text-gray-500 text-xs">We'll call you back soon</p>
                  </div>
                </div>

                {submitStatus === 'success' ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 rounded-3xl bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 flex items-center justify-center mx-auto mb-4">
                      <i className="ri-checkbox-circle-fill text-green-500 text-4xl"></i>
                    </div>
                    <h4 className="text-gray-900 dark:text-white font-black text-xl mb-2">Complaint Registered!</h4>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">Our team will call you within a few hours on your provided number.</p>
                  </div>
                ) : (
                  <form
                    action="https://readdy.ai/api/form/d76bdcjq7u9ee9hceccg"
                    method="POST"
                    onSubmit={handleSubmit}
                    className="space-y-4"
                    data-readdy-form
                  >
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 dark:text-gray-300 text-xs font-bold mb-1.5 uppercase tracking-wide">Your Name *</label>
                        <input type="text" name="name" required value={formData.name} onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-white text-sm placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none transition-all"
                          placeholder="Full name" />
                      </div>
                      <div>
                        <label className="block text-gray-700 dark:text-gray-300 text-xs font-bold mb-1.5 uppercase tracking-wide">Phone Number *</label>
                        <input type="tel" name="phone" required pattern="[0-9]{10}" maxLength={10} value={formData.phone} onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-white text-sm placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none transition-all"
                          placeholder="10 digit mobile" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 dark:text-gray-300 text-xs font-bold mb-1.5 uppercase tracking-wide">Issue Type *</label>
                        <select name="complaintType" required value={formData.complaintType} onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-white text-sm focus:border-orange-400 outline-none transition-all cursor-pointer appearance-none"
                          style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
                        >
                          <option value="" disabled>Select issue type</option>
                          {complaintTypes.map((type) => <option key={type} value={type}>{type}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-gray-700 dark:text-gray-300 text-xs font-bold mb-1.5 uppercase tracking-wide">Order ID (Optional)</label>
                        <input type="text" name="orderId" value={formData.orderId} onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-white text-sm placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-400 outline-none transition-all"
                          placeholder="ORD123456" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-gray-700 dark:text-gray-300 text-xs font-bold mb-1.5 uppercase tracking-wide">Describe Issue *</label>
                      <textarea name="message" required rows={4} maxLength={500} value={formData.message} onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-white text-sm placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-400 outline-none transition-all resize-none"
                        placeholder="Describe your issue in detail..."
                      ></textarea>
                      <p className="text-gray-400 dark:text-gray-500 text-xs mt-1 text-right">{formData.message.length}/500</p>
                    </div>
                    <div className="flex items-start gap-3 p-3.5 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-700/40">
                      <i className="ri-information-line text-amber-500 text-lg mt-0.5 shrink-0"></i>
                      <p className="text-gray-600 dark:text-gray-300 text-xs leading-relaxed">After submitting, our support team will review your complaint and call you within a few hours on the provided phone number.</p>
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-4 rounded-xl font-black text-sm text-white flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60 hover:opacity-90 transition-all"
                      style={{ background: 'linear-gradient(135deg, #FF5C00, #FF1744)' }}
                    >
                      {isSubmitting ? <><i className="ri-loader-4-line animate-spin"></i> Submitting...</> : <><i className="ri-send-plane-fill"></i> Submit Complaint</>}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
